import React, { useEffect, useMemo, useState } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { aiAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import { refreshAiStatus } from '../../hooks/useAiStatus';
import HelpHint from '../help/HelpHint';
import { AI_FEATURE_LABELS, AiModelOption, AiSettings } from '../../types/ai';

const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const formatPrice = (model: AiModelOption) => {
  if (model.isFree) return 'ingyenes';
  if (model.promptPricePerMillion == null) return '';
  return `$${model.promptPricePerMillion} / $${model.completionPricePerMillion ?? '?'} per M token`;
};

/**
 * Az AI funkciók admin beállítása a Rendszerbeállítások oldalon: be/ki,
 * modellválasztás az OpenRouter kínálatából, napi korlátok, személyes adat
 * engedélyezése, kapcsolatpróba és felhasználási statisztika.
 */
const AiSettingsPanel: React.FC = () => {
  const { showToast } = useToast();
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [models, setModels] = useState<AiModelOption[]>([]);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [onlyFree, setOnlyFree] = useState(true);
  const [search, setSearch] = useState('');

  const [enabled, setEnabled] = useState(false);
  const [model, setModel] = useState('');
  const [dailyLimit, setDailyLimit] = useState('200');
  const [userDailyLimit, setUserDailyLimit] = useState('30');
  const [allowPersonalData, setAllowPersonalData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testReply, setTestReply] = useState<string | null>(null);

  const apply = (s: AiSettings) => {
    setSettings(s);
    setEnabled(s.enabled);
    setModel(s.model ?? '');
    setDailyLimit(String(s.dailyLimit));
    setUserDailyLimit(String(s.userDailyLimit));
    setAllowPersonalData(s.allowPersonalData);
  };

  useEffect(() => {
    aiAPI
      .getSettings()
      .then(apply)
      .catch((err) => showToast(getErrorMessage(err, 'Az AI beállítások nem tölthetők be.'), 'error'));
    aiAPI
      .getModels()
      .then(setModels)
      .catch((err) => setModelsError(getErrorMessage(err, 'A modellek listája most nem érhető el.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredModels = useMemo(() => {
    const q = search.trim().toLowerCase();
    return models.filter(
      (m) =>
        (!onlyFree || m.isFree || m.id === model) &&
        (!q || m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q))
    );
  }, [models, onlyFree, search, model]);

  const selectedModel = models.find((m) => m.id === model);

  const save = async () => {
    const daily = parseInt(dailyLimit, 10);
    const perUser = parseInt(userDailyLimit, 10);
    if (!Number.isInteger(daily) || daily < 1 || !Number.isInteger(perUser) || perUser < 1) {
      showToast('A napi korlát legalább 1 legyen.', 'error');
      return;
    }
    if (enabled && !model) {
      showToast('Az AI bekapcsolásához válassz modellt.', 'error');
      return;
    }

    setSaving(true);
    try {
      const saved = await aiAPI.updateSettings({
        enabled,
        model: model || null,
        dailyLimit: daily,
        userDailyLimit: perUser,
        allowPersonalData,
      });
      apply(saved);
      refreshAiStatus();
      showToast(saved.enabled ? 'Az AI funkciók be vannak kapcsolva.' : 'AI beállítások mentve.', 'success');
    } catch (err) {
      showToast(getErrorMessage(err, 'A beállítást nem sikerült menteni.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestReply(null);
    try {
      const result = await aiAPI.test();
      setTestReply(`${result.reply} (${result.model}, ${(result.elapsedMs / 1000).toFixed(1)} mp)`);
    } catch (err) {
      showToast(getErrorMessage(err, 'A kapcsolatpróba nem sikerült.'), 'error');
    } finally {
      setTesting(false);
    }
  };

  const dirty =
    !!settings &&
    (enabled !== settings.enabled ||
      model !== (settings.model ?? '') ||
      dailyLimit !== String(settings.dailyLimit) ||
      userDailyLimit !== String(settings.userDailyLimit) ||
      allowPersonalData !== settings.allowPersonalData);

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden mt-6">
      <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex items-center gap-2">
        <SparklesIcon className="h-5 w-5 text-primary-600" />
        <h2 className="text-base font-semibold text-gray-900">AI asszisztens</h2>
        <HelpHint id="system-settings.ai" />
      </div>
      {!settings ? (
        <div className="p-4 md:p-6 text-sm text-gray-500">Betöltés…</div>
      ) : (
        <div className="p-4 md:p-6 space-y-5">
          <p className="text-sm text-gray-500">
            Edzésterv, gyakorlatrajz, bejelentés, mérkőzés-beszámoló, heti összefoglaló, játékos-értékelés és
            kérdések az adatokról - az OpenRouteren keresztül választott modellel. A játékosok neve helyett az AI
            csak jelölést kap, TAJ szám, lakcím és egészségügyi adat nem megy ki.
          </p>

          {!settings.apiKeyConfigured && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Nincs beállítva az OpenRouter API kulcs a szerveren. Hozz létre egyet az{' '}
              <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer" className="font-medium underline">
                openrouter.ai/keys
              </a>{' '}
              oldalon, és írd be a szerver <span className="font-mono">appsettings.json</span> fájljába
              (<span className="font-mono">OpenRouter:ApiKey</span>). Addig a modellt már kiválaszthatod.
            </div>
          )}

          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <div className="font-medium text-gray-900">AI funkciók bekapcsolása</div>
              <div className="text-sm text-gray-500">
                Az edzők és az adminok látják az AI gombokat. Kikapcsolva minden AI gomb eltűnik.
              </div>
            </div>
          </label>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              Modell
              <HelpHint id="system-settings.aiModel" />
            </div>
            {modelsError ? (
              <>
                <p className="text-sm text-red-600">{modelsError}</p>
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="Modell azonosító, pl. google/gemma-4-31b-it:free"
                  className={inputClass}
                  aria-label="Modell azonosító"
                />
              </>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Keresés, pl. gemma"
                    className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label="Modell keresése"
                  />
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={onlyFree}
                      onChange={(e) => setOnlyFree(e.target.checked)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    Csak ingyenes
                  </label>
                </div>
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className={inputClass}
                  aria-label="Modell"
                >
                  <option value="">{models.length === 0 ? 'Modellek betöltése…' : 'Válassz modellt…'}</option>
                  {model && !models.some((m) => m.id === model) && <option value={model}>{model}</option>}
                  {filteredModels.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} - {formatPrice(m)}
                    </option>
                  ))}
                </select>
                {selectedModel && (
                  <p className="text-xs text-gray-500">
                    <span className="font-mono">{selectedModel.id}</span>
                    {selectedModel.contextLength ? ` · ${selectedModel.contextLength.toLocaleString('hu-HU')} token kontextus` : ''}
                    {selectedModel.supportsImages ? ' · képet is ért' : ' · képet nem ért (a dokumentumból kitöltéshez szöveget kell beilleszteni)'}
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  Az ingyenes modelleknek napi és percenkénti korlátja van, és túlterheltség esetén ritkán hibát adnak.
                  Később bármikor válthatsz fizetős modellre - ehhez az OpenRouter fiókban egyenleg kell.
                </p>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-sm font-medium text-gray-700">
              Napi korlát a klubnak
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={10000}
                value={dailyLimit}
                onChange={(e) => setDailyLimit(e.target.value)}
                className={`mt-1 ${inputClass}`}
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Napi korlát felhasználónként
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={10000}
                value={userDailyLimit}
                onChange={(e) => setUserDailyLimit(e.target.value)}
                className={`mt-1 ${inputClass}`}
              />
            </label>
          </div>

          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={allowPersonalData}
              onChange={(e) => setAllowPersonalData(e.target.checked)}
              className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <div>
              <div className="font-medium text-gray-900">Dokumentumból kitöltés engedélyezése</div>
              <div className="text-sm text-gray-500">
                Új játékos felvételénél az admin feltölthet egy kitöltött jelentkezési lapot, és az AI kiolvassa belőle
                az adatokat. Ilyenkor a dokumentum (név, születési adatok, TAJ szám, lakcím) az AI szolgáltatóhoz kerül,
                amit az ingyenes modellek naplózhatnak. Csak akkor kapcsold be, ha ez összefér a klub adatkezelési
                tájékoztatójával.
              </div>
            </div>
          </label>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={test}
              disabled={testing || dirty || !settings.model || !settings.apiKeyConfigured}
              title={dirty ? 'Előbb mentsd a beállítást' : undefined}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {testing ? 'Próba…' : 'Kapcsolat kipróbálása'}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Mentés…' : 'Mentés'}
            </button>
          </div>
          {testReply && (
            <p className="rounded-lg bg-green-50 p-3 text-sm text-green-800" role="status">
              {testReply}
            </p>
          )}

          <div className="border-t border-gray-100 pt-4">
            <div className="text-sm font-medium text-gray-900">
              Felhasználás: ma {settings.usedToday} / {settings.dailyLimit} kérés
            </div>
            {settings.usageLast30Days.length > 0 ? (
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                {settings.usageLast30Days.map((u) => (
                  <li key={u.feature} className="flex justify-between gap-4">
                    <span>{AI_FEATURE_LABELS[u.feature] ?? u.feature}</span>
                    <span className="tabular-nums">
                      {u.count} sikeres{u.failed > 0 ? `, ${u.failed} sikertelen` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-sm text-gray-500">Az elmúlt 30 napban még nem használta senki.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AiSettingsPanel;
